import React, { useState, useEffect } from 'react';
import { SponsorService, Invitation } from '../services/sponsorService';
import styles from '../styles/invitationsPanel.module.css';
import { useLocale } from '../context/LocaleContext';

interface InvitationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onInvitationResponded?: () => void;
}

const InvitationsPanel: React.FC<InvitationsPanelProps> = ({
  isOpen,
  onClose,
  onInvitationResponded
}) => {
  const { t } = useLocale();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [responding, setResponding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadInvitations();
    }
  }, [isOpen]);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await SponsorService.getPendingInvitations();
      setInvitations(data);
      
      // Auto-close if no pending invitations
      if (data.length === 0) {
        setTimeout(() => onClose(), 100);
      }
    } catch (err: any) {
      setError(t('auth.invite.panel.load_failed'));
      console.error('Error loading invitations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (invitationId: string, response: 'accept' | 'decline') => {
    try {
      setResponding(invitationId);
      setError(null);
      await SponsorService.respondToInvitation(invitationId, response);
      
      // Reload invitations
      await loadInvitations();
      
      // Notify parent
      if (onInvitationResponded) {
        onInvitationResponded();
      }
      
      // Close if no more invitations
      if (invitations.length === 1) {
        setTimeout(() => onClose(), 1000);
      }
    } catch (err: any) {
      setError(t(response === 'accept' ? 'auth.invite.panel.accept_failed' : 'auth.invite.panel.decline_failed'));
      console.error(`Error ${response}ing invitation:`, err);
    } finally {
      setResponding(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const relationshipLabels = {
    sponsor: t('auth.invite.panel.as_sponsor'),
    sponsee: t('auth.invite.panel.as_sponsee'),
    friend: t('auth.invite.panel.as_friend'),
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {invitations.length > 0
              ? t('auth.invite.panel.pending_title', { count: invitations.length })
              : t('auth.invite.panel.title')}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {loading && (
            <div className={styles.loading}>{t('auth.invite.panel.loading')}</div>
          )}

          {error && (
            <div className={styles.error}>{error}</div>
          )}

          {!loading && invitations.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📬</div>
              <p>{t('auth.invite.panel.empty')}</p>
            </div>
          )}

          {!loading && invitations.length > 0 && (
            <div className={styles.invitationsList}>
              {invitations.map((invitation) => (
                <div key={invitation.invitation_id} className={styles.invitationCard}>
                  <div className={styles.invitationHeader}>
                    <div className={styles.fromInfo}>
                      <div className={styles.fromName}>{invitation.from_name}</div>
                      <div className={styles.fromEmail}>{invitation.from_email}</div>
                    </div>
                    <div className={styles.relationshipBadge}>
                      {relationshipLabels[invitation.relationship_type]}
                    </div>
                  </div>

                  {invitation.message && (
                    <div className={styles.message}>
                      <div className={styles.messageLabel}>{t('auth.invite.panel.personal_message')}</div>
                      <div className={styles.messageText}>{invitation.message}</div>
                    </div>
                  )}

                  {invitation.program && (
                    <div className={styles.program}>
                      {t('auth.invite.panel.program')} <strong>{invitation.program}</strong>
                    </div>
                  )}

                  <div className={styles.invitationActions}>
                    <button
                      onClick={() => handleRespond(invitation.invitation_id, 'decline')}
                      className={styles.declineButton}
                      disabled={responding !== null}
                    >
                      {responding === invitation.invitation_id ? t('auth.invite.panel.declining') : t('auth.invite.panel.decline')}
                    </button>
                    <button
                      onClick={() => handleRespond(invitation.invitation_id, 'accept')}
                      className={styles.acceptButton}
                      disabled={responding !== null}
                    >
                      {responding === invitation.invitation_id ? t('auth.invite.panel.accepting') : t('auth.invite.panel.accept')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitationsPanel;

